import {
  FieldProps,
  FormikContextType,
  FormikProps,
  getIn,
  setIn,
} from 'formik';

import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import { RegexExpWithMsg } from 'utils/helpers';
import { TextFieldProps } from 'formik-material-ui';
import { VALIDATION_MAX_LENGTH_MESSAGE } from 'config';
import memoize from 'lodash/memoize';

export type CustomEventProps = {
  form: FieldProps['form'];
  field: FieldProps['field'];
  temporalError: string;
  setTemporalError: React.Dispatch<string>;
  errMsgTime: number;
  setErrMsgTime: React.Dispatch<any>;
  limit: boolean;
  setLimit: React.Dispatch<boolean>;
};

// use case: control submit button disabled state
export const disableSubmit = <Values>(
  formik: FormikContextType<Values> | FormikProps<Values>
): boolean => {
  return (
    getIn(formik.status, 'isAsyncValidating') ||
    !formik.isValid ||
    !formik.dirty ||
    formik.isSubmitting
  );
};

// check field required property in Yup schema
// TOFIX: not working with Yup.when
export const isRequiredField = <Values>(
  formikProps: FormikContextType<Values>,
  fieldname: string
): boolean => {
  return !!findFieldParameterInValidationSchema(
    formikProps.validationSchema,
    fieldname,
    'required'
  );
};

// get max length set in yup validation
export const getMaxLength = <Values>(
  formikContext: FormikContextType<Values>,
  fieldname: string
): number | undefined => {
  const foundParameter = findFieldParameterInValidationSchema(
    formikContext.validationSchema,
    fieldname,
    'max'
  );

  return foundParameter?.params?.max;
};

// DO NOT USE DIRECTLY, use findFieldParameterInValidationSchema instead
const findFieldParameterInValidationSchemaExpensive = (
  validationSchema,
  fieldname: string,
  param: string
): { name: string; params: any } | undefined => {
  const isValidSchema =
    !!validationSchema && typeof validationSchema.describe === 'function';
  if (!isValidSchema) {
    return undefined;
  }

  // is array field / ex: array.0.field | array[0].field
  const isArrayFormatRegx = /\.\d+\.?|\[\d+\]\.?/;
  if (fieldname.match(isArrayFormatRegx)) {
    const [arrayFieldGroup, arrayFieldName] = fieldname.split(
      isArrayFormatRegx
    ) ?? ['', ''];

    const { innerType } = validationSchema.describe()?.fields?.[
      arrayFieldGroup
    ];

    const tests =
      innerType?.type === 'object'
        ? innerType?.fields?.[arrayFieldName]?.tests
        : innerType?.tests;

    return tests?.find(({ name }) => name === param);
  }

  return getIn(validationSchema.describe().fields, fieldname)?.tests.find(
    ({ name }) => name === param
  );
};

const findFieldParameterInValidationSchema = memoize(
  findFieldParameterInValidationSchemaExpensive,
  (...args) => JSON.stringify(args)
);

export const blockMaxTyping = (
  e: React.KeyboardEvent,
  max: number,
  {
    form,
    field,
    temporalError,
    setTemporalError,
    limit,
    setLimit,
    errMsgTime,
    setErrMsgTime,
  }: CustomEventProps,
  showTypingErrors
) => {
  if (e.key === 'Backspace' || e.key === 'Delete') {
    setTemporalError('');
    return;
  }
  const fieldLength: number = field.value?.length;
  const maxParam = Number(max);
  const errorMessage = VALIDATION_MAX_LENGTH_MESSAGE;

  if (maxParam > 0) {
    setLimit(fieldLength + 1 >= maxParam);
  }

  if (showTypingErrors) {
    if (limit && fieldLength === maxParam && e.key.length === 1) {
      setError(errorMessage, { form, field }, setTemporalError, setErrMsgTime);
    } else if (limit && errorMessage === temporalError) {
      setTemporalError('');
    }
  }
};

export const blockPaste = (
  e: React.ClipboardEvent,
  regexObj: RegexExpWithMsg,
  {
    form,
    field,
    temporalError,
    setTemporalError,
    errMsgTime,
    setErrMsgTime,
  }: CustomEventProps
) => {
  const text = e.clipboardData && e.clipboardData.getData('text/plain');

  if (!regexObj) return;
  if (!regexObj.regex.test(text)) {
    e.preventDefault();
    setError(
      regexObj.errorMessage,
      { form, field },
      setTemporalError,
      setErrMsgTime
    );
  } else if (temporalError === regexObj.errorMessage) {
    setTemporalError('');
  }
};

export const blockTyping = (
  e: React.KeyboardEvent,
  regexObj: RegexExpWithMsg,
  {
    form,
    field,
    temporalError,
    setTemporalError,
    errMsgTime,
    setErrMsgTime,
  }: CustomEventProps
) => {
  if (!regexObj) return;
  if (e.key === 'Enter') {
    setTemporalError('');
    return;
  }
  if (!regexObj.regex.test(e.key)) {
    clearTimeout(errMsgTime);
    e.preventDefault();
    setError(
      regexObj.errorMessage,
      { form, field },
      setTemporalError,
      setErrMsgTime
    );
  } else if (temporalError === regexObj.errorMessage) {
    setTemporalError('');
  }
};

const setError = (
  errorMessage: string,
  { form, field }: Partial<FieldProps<any>>,
  setErrorCallback: React.Dispatch<string>,
  setTimer: React.Dispatch<any>
) => {
  if (form) {
    form.setFieldTouched(field!.name, true, false);
  }
  setErrorCallback(errorMessage);
  setTimer(setTimeout(() => setErrorCallback(''), 2000));
};

export const mapToSelectStyleProps = ({
  inline,
  formik,
  field,
  label,
}: Pick<FieldProps<any>, 'field'> &
  Pick<MuiTextFieldProps, 'label'> & {
    inline?: boolean;
    formik: FormikContextType<any>;
  }): Pick<MuiTextFieldProps, 'required' | 'InputLabelProps' | 'label'> => {
  const fieldValue = formik.values[field.name];
  // switch between inline styling (default / inline)
  const isRequired =
    inline && fieldValue ? false : isRequiredField(formik, field.name);
  const labelContent = inline && fieldValue ? ' ' : label;
  const inputLabelProps = inline ? {} : { shrink: true };

  return {
    required: isRequired,
    InputLabelProps: inputLabelProps,
    label: labelContent,
  };
};

/**
 * Map formik text field props to material ui TextFields
 * controls when errors are shown & pass props
 */
type CustomTextFieldProps = TextFieldProps & {
  temporalError?: string;
  showErrorOnMount?: boolean;
};
export const customFieldToTextField = ({
  temporalError,
  disabled,
  form: { isSubmitting, touched, errors, validateOnMount, status },
  field,
  showErrorOnMount = true,
  ...restProps
}: CustomTextFieldProps): MuiTextFieldProps => {
  const mergeFormikStatus = {
    showErrorsOnMount: validateOnMount && showErrorOnMount,
    ...status,
  };

  const mergeFormErrors = temporalError
    ? setIn(errors, field.name, temporalError)
    : errors;
  const readOnly = !!restProps.InputProps?.readOnly;
  const fieldError = getIn(mergeFormErrors, field.name);

  const displayError =
    !readOnly &&
    !!fieldError &&
    (getIn(touched, field.name) || mergeFormikStatus.showErrorsOnMount);

  return {
    ...restProps,
    ...field,
    error: displayError,
    helperText: displayError ? fieldError : restProps.helperText,
    disabled: disabled ?? isSubmitting,
    variant: restProps.variant,
  };
};
